import { useEffect, useState, useMemo } from 'react';
import { 
    Box, 
    Typography, 
    Button, 
    Paper, 
    Dialog, 
    DialogContent, 
    TextField, 
    Chip, 
    Autocomplete, 
    FormControlLabel, 
    Checkbox 
} from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import supabase from '../../../supabaseClient';

const typeLabels = {
    surowiec: 'surowiec',
    element: 'element',
    produkt: 'produkt',
};

const emptyForm = { product_id: null, quantity: '', transaction_date: new Date().toISOString().slice(0, 10), source: 'produkcja', note: '' };


function WarehouseCategory({ productType }) {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [orderedByProduct, setOrderedByProduct] = useState({});

    const [contractors, setContractors] = useState([]);
    const [loadingContractors, setLoadingContractors] = useState(false);

    const [transactions, setTransactions] = useState([]);
    const [transactionsCount, setTransactionsCount] = useState(0);
    const [loadingTransactions, setLoadingTransactions] = useState(true);
    const [transactionsPaginationModel, setTransactionsPaginationModel] = useState({ page: 0, pageSize: 25 });
    const [searchQuery, setSearchQuery] = useState(''); // Filtr tekstowy dla historii
    const [debouncedSearchQuery, setDebouncedSearchQuery] = useState('');

    const [modalOpen, setModalOpen] = useState(false);
    const [modalMode, setModalMode] = useState('przyjecie'); // 'przyjecie' | 'wydanie'
    const [form, setForm] = useState(emptyForm);
    const [saving, setSaving] = useState(false);
    const [errorMsg, setErrorMsg] = useState('');

    const [detailsTarget, setDetailsTarget] = useState(null);
    const [idConfiguration, setIdConfiguration] = useState(null);

    useEffect(() => {
        const getInitialData = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session) {
                const { data: profileData } = await supabase
                    .from('profiles')
                    .select('id_configuration')
                    .eq('id', session.user.id)
                    .single();

                if (profileData) {
                    setIdConfiguration(profileData.id_configuration);
                }
            }
        };
        getInitialData();
    }, []);

    async function fetchProducts() {
        setLoading(true);
        const { data, error } = await supabase
            .from('products')
            .select('*')
            .eq('id_configuration', idConfiguration)
            .eq('type', productType)
            .order('name');

        if (error) {
            console.error('Błąd pobierania pozycji magazynu:', error);
            setLoading(false);
            return;
        }

        setRows(data);
        setLoading(false);
    }

    async function fetchContractors() {
        if (!idConfiguration) return;
        setLoadingContractors(true);
        const { data, error } = await supabase
            .from('kontrahenci')
            .select('id, name, nip')
            .eq('id_configuration', idConfiguration)
            .order('name');

        if (error) {
            console.error('Błąd pobierania kontrahentów:', error);
            setLoadingContractors(false);
            return;
        }

        setContractors(data || []);
        setLoadingContractors(false);
    }

    async function fetchOrderedQuantities() {
        if (productType !== 'produkt') return;

        const { data, error } = await supabase
            .from('order_items')
            .select('product_id, amount, orders!inner(realizacja, id_configuration)')
            .eq('orders.id_configuration', idConfiguration)
            .in('orders.realizacja', ['nowe', 'w_realizacji']);

        if (error) {
            console.error('Błąd pobierania zamówionych ilości:', error);
            return;
        }

        const grouped = {};
        data.forEach((item) => {
            if (!item.product_id) return;
            grouped[item.product_id] = (grouped[item.product_id] ?? 0) + Number(item.amount);
        });
        setOrderedByProduct(grouped);
    }

    async function fetchTransactions(page, pageSize, query) {
        setLoadingTransactions(true);
        const from = page * pageSize;
        const to = from + pageSize - 1;

        let request = supabase
            .from('inventory_transactions')
            .select('id, transaction_type, source, quantity, transaction_date, document, note, products!inner(name, type)', { count: 'exact' })
            .eq('id_configuration', idConfiguration)
            .eq('products.type', productType);

        if (query) {
            request = request.or(`document.ilike.%${query}%,products.name.ilike.%${query}%`);
        }

        const { data, error, count } = await request
            .order('transaction_date', { ascending: false })
            .range(from, to);

        if (error) {
            console.error('Błąd pobierania historii transakcji:', error);
            setLoadingTransactions(false);
            return;
        }

        setTransactions(data || []);
        setTransactionsCount(count ?? 0);
        setLoadingTransactions(false);
    }

    useEffect(() => {
        if (!idConfiguration) return;
        fetchProducts();
        fetchContractors();
        fetchOrderedQuantities();
    }, [idConfiguration, productType]);

    // Debounce wyszukiwania, aby nie odpytywać bazy przy każdym znaku
    useEffect(() => {
        const timeout = setTimeout(() => setDebouncedSearchQuery(searchQuery.trim()), 400);
        return () => clearTimeout(timeout);
    }, [searchQuery]);

    // Powrót na pierwszą stronę przy zmianie wyszukiwania/kategorii
    useEffect(() => {
        setTransactionsPaginationModel((prev) => (prev.page === 0 ? prev : { ...prev, page: 0 }));
    }, [debouncedSearchQuery, productType]);

    useEffect(() => {
        if (!idConfiguration) return;
        fetchTransactions(transactionsPaginationModel.page, transactionsPaginationModel.pageSize, debouncedSearchQuery);
    }, [idConfiguration, productType, transactionsPaginationModel, debouncedSearchQuery]);

    // Przygotowanie płaskich danych dla DataGrid (aby renderowanie kolumn działało natywnie)
    const preparedTransactions = useMemo(() => {
        return transactions.map((t) => ({
            ...t,
            product_name: t.products?.name ?? '—',
            contractor_display: t.kontrahenci
                ? t.kontrahenci.nip
                    ? `${t.kontrahenci.name} (${t.kontrahenci.nip})`
                    : t.kontrahenci.name
                : '—',
            contractor_name: t.kontrahenci?.name ?? '',
        }));
    }, [transactions]);

    const openModal = (mode) => {
        setModalMode(mode);
        setForm(emptyForm);
        setErrorMsg('');
        setModalOpen(true);
    };

    const handleFieldChange = (field) => (e) => {
        setForm((prev) => ({ ...prev, [field]: e.target.value }));
    };

    const isPurchase = modalMode === 'przyjecie' && (productType !== 'produkt' || form.source === 'zakup');

    const handleSave = async () => {
        if (!form.product_id || !(Number(form.quantity) > 0)) {
            setErrorMsg('Wybierz pozycję i podaj ilość większą od zera.');
            return;
        }

        setSaving(true);
        setErrorMsg('');

        const product = rows.find((r) => r.id === form.product_id);
        const currentStock = Number(product?.stock_quantity ?? 0);
        const delta = modalMode === 'przyjecie' ? Number(form.quantity) : -Number(form.quantity);
        const newStock = currentStock + delta;

        const transactionPayload = {
            product_id: form.product_id,
            transaction_type: modalMode,
            source: productType === 'produkt' && modalMode === 'przyjecie' ? form.source : null,
            quantity: Number(form.quantity),
            unit_price: isPurchase && form.unit_price !== '' ? Number(form.unit_price) : null,
            contractor_id: isPurchase ? form.contractor_id : null,
            transaction_date: form.transaction_date,
            document: modalMode === 'przyjecie' ? 'Przyjęcie ręczne' : 'Wydanie ręczne',
            note: form.note.trim() || null,
            is_correction: form.is_correction,
            id_configuration: idConfiguration,
        };

        const { error: transactionError } = await supabase
            .from('inventory_transactions')
            .insert(transactionPayload);

        if (transactionError) {
            console.error('Błąd zapisu transakcji:', transactionError);
            setErrorMsg('Nie udało się zapisać transakcji.');
            setSaving(false);
            return;
        }

        const { error: stockError } = await supabase
            .from('products')
            .update({ stock_quantity: newStock })
            .eq('id', form.product_id);

        setSaving(false);

        if (stockError) {
            console.error('Błąd aktualizacji stanu:', stockError);
            setErrorMsg('Transakcja zapisana, ale nie udało się zaktualizować stanu magazynowego.');
            return;
        }

        setModalOpen(false);
        fetchProducts();
        fetchOrderedQuantities();
        setTransactionsPaginationModel((prev) => {
            if (prev.page === 0) {
                fetchTransactions(0, prev.pageSize, debouncedSearchQuery);
                return prev;
            }
            return { ...prev, page: 0 };
        });
    };

    const columns = [
        { field: 'name', headerName: 'NAZWA', flex: 1.6, minWidth: 200, renderCell: (params) => <strong>{params.value}</strong> },
        { field: 'stock_quantity', headerName: 'STAN', flex: 1, minWidth: 100 },
        ...(productType === 'produkt'
            ? [
                  {
                      field: 'ordered',
                      headerName: 'ZAMÓWIONE (NOWE/W REALIZACJI)',
                      flex: 1.4,
                      minWidth: 180,
                      renderCell: (params) => {
                          const ordered = orderedByProduct[params.row.id] ?? 0;
                          return (
                              <Chip
                                  label={ordered}
                                  size="small"
                                  sx={{ bgcolor: ordered > 0 ? '#F7EEE3' : '#F1EDE6', color: ordered > 0 ? '#C97A22' : '#6B6B6B', fontWeight: 600, borderRadius: '8px' }}
                              />
                          );
                      },
                  },
                  {
                      field: 'afterFulfillment',
                      headerName: 'SZACUNKOWY STAN PO REALIZACJI',
                      flex: 1.2,
                      minWidth: 150,
                      renderCell: (params) => {
                          const ordered = orderedByProduct[params.row.id] ?? 0;
                          const result = Number(params.row.stock_quantity ?? 0) - ordered;
                          return (
                              <Typography sx={{ fontWeight: 700, color: result < 0 ? '#C7433F' : '#2E8552' }}>
                                  {result}
                              </Typography>
                          );
                      },
                  },
              ]
            : []),
    ];

    const transactionColumns = [
        { field: 'transaction_date', headerName: 'DATA', flex: 0.8, minWidth: 110 },
        {
            field: 'transaction_type',
            headerName: 'TYP',
            flex: 1.2,
            minWidth: 160,
            renderCell: (params) => (
                <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                    <Chip
                        label={params.value === 'przyjecie' ? 'Przyjęcie (PZ)' : 'Wydanie (WZ)'}
                        size="small"
                        sx={{
                            bgcolor: params.value === 'przyjecie' ? '#E4F3E9' : '#FBE6E6',
                            color: params.value === 'przyjecie' ? '#2E8552' : '#C7433F',
                            fontWeight: 600,
                            borderRadius: '8px',
                        }}
                    />
                    {params.row.is_correction && (
                        <Chip
                            label="Korekta"
                            size="small"
                            sx={{ bgcolor: '#FDF0D5', color: '#B8860B', fontWeight: 700, borderRadius: '8px' }}
                        />
                    )}
                </Box>
            ),
        },
        { field: 'product_name', headerName: 'POZYCJA', flex: 1.4, minWidth: 180 },
        {
            field: 'quantity',
            headerName: 'ILOŚĆ',
            type: 'number',
            flex: 0.8,
            minWidth: 90,
            renderCell: (params) => (
                <span style={{ color: params.row.transaction_type === 'wydanie' ? '#C7433F' : '#2E8552', fontWeight: 600 }}>
                    {params.row.transaction_type === 'wydanie' ? '-' : '+'}
                    {params.value}
                </span>
            ),
        },
        {
            field: 'unit_price',
            headerName: 'CENA JEDN.',
            type: 'number',
            flex: 1,
            minWidth: 110,
            valueFormatter: (value) => (value !== null && value !== undefined ? `${value} zł` : '—'),
        },
        {
            field: 'contractor_display',
            headerName: 'KONTRAHENT',
            flex: 1.5,
            minWidth: 180,
        },
        { field: 'document', headerName: 'DOKUMENT', flex: 1.2, minWidth: 150 },
        {
            field: 'details',
            headerName: '',
            flex: 0.7,
            minWidth: 100,
            sortable: false,
            filterable: false,
            renderCell: (params) => (
                <Button
                    size="small"
                    onClick={() => setDetailsTarget(params.row)}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '8px' }}
                >
                    Szczegóły
                </Button>
            ),
        },
    ];

    return (
        <Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5, mb: 3 }}>
                <Button
                    variant="outlined"
                    onClick={() => openModal('wydanie')}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}
                >
                    + Wydanie
                </Button>
                <Button
                    variant="contained"
                    onClick={() => openModal('przyjecie')}
                    sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}
                >
                    + Przyjęcie
                </Button>
            </Box>

            <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #ECE7DE', mb: 4 }}>
                <DataGrid
                    rows={rows}
                    columns={columns}
                    loading={loading}
                    disableRowSelectionOnClick
                    disableColumnMenu
                    hideFooter={rows.length <= 100}
                    columnHeaderHeight={44}
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': { backgroundColor: '#FAF7F2' },
                        '& .MuiDataGrid-columnHeaderTitle': { color: 'text.secondary', fontWeight: 700, fontSize: '12px', letterSpacing: '0.03em' },
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-row:hover': { backgroundColor: '#FEFCF9' },
                    }}
                />
            </Paper>

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1.5, flexWrap: 'wrap', gap: 2 }}>
                <Typography sx={{ fontSize: '16px', fontWeight: 700 }}>
                    Historia transakcji (produkcja / zakup / wydania pod zamówienia)
                </Typography>
                
                {/* Wyszukiwarka kontekstowa dla tabeli historii */}
                <TextField
                    placeholder="Szukaj (pozycja, dokument)..."
                    size="small"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    sx={{ minWidth: 260, bg: '#fff' }}
                />
            </Box>

            <Paper elevation={0} sx={{ borderRadius: 3, overflow: 'hidden', border: '1px solid #ECE7DE' }}>
                <DataGrid
                    rows={preparedTransactions}
                    columns={transactionColumns}
                    loading={loadingTransactions}
                    disableRowSelectionOnClick
                    paginationMode="server"
                    rowCount={transactionsCount}
                    paginationModel={transactionsPaginationModel}
                    onPaginationModelChange={setTransactionsPaginationModel}
                    pageSizeOptions={[25, 50, 100]}
                    columnHeaderHeight={44}
                    sx={{
                        border: 'none',
                        '& .MuiDataGrid-columnHeaders': { backgroundColor: '#FAF7F2' },
                        '& .MuiDataGrid-columnHeaderTitle': { color: 'text.secondary', fontWeight: 700, fontSize: '12px', letterSpacing: '0.03em' },
                        '& .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeader:focus-within': { outline: 'none' },
                        '& .MuiDataGrid-row:hover': { backgroundColor: '#FEFCF9' },
                    }}
                />
            </Paper>

            {/* Modal przyjęcia / wydania */}
            <Dialog open={modalOpen} onClose={() => setModalOpen(false)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogContent sx={{ p: 4 }}>
                    <Typography sx={{ fontSize: '20px', fontWeight: 700, color: 'primary.main', mb: 3 }}>
                        {modalMode === 'przyjecie' ? 'Przyjęcie' : 'Wydanie'} — {typeLabels[productType]}
                    </Typography>

                    {productType === 'produkt' && modalMode === 'przyjecie' && (
                        <>
                            <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5 }}>Źródło</Typography>
                            <Box sx={{ display: 'flex', gap: 1, mb: 2.5 }}>
                                {[
                                    { value: 'produkcja', label: 'Z produkcji' },
                                    { value: 'zakup', label: 'Zakupione' },
                                ].map((opt) => (
                                    <Button
                                        key={opt.value}
                                        onClick={() => setForm((prev) => ({ ...prev, source: opt.value }))}
                                        variant={form.source === opt.value ? 'contained' : 'outlined'}
                                        sx={{ flex: 1, textTransform: 'none', fontWeight: 700, borderRadius: 2, py: 1 }}
                                    >
                                        {opt.label}
                                    </Button>
                                ))}
                            </Box>
                        </>
                    )}

                    <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5 }}>
                        {productType === 'produkt' ? 'Produkt' : productType === 'element' ? 'Element' : 'Surowiec'}
                    </Typography>
                    <Autocomplete
                        options={rows}
                        getOptionLabel={(option) => option.name ?? ''}
                        value={rows.find((r) => r.id === form.product_id) ?? null}
                        onChange={(_, value) => setForm((prev) => ({ ...prev, product_id: value?.id ?? null }))}
                        isOptionEqualToValue={(option, value) => option.id === value.id}
                        renderInput={(params) => <TextField {...params} placeholder="Wybierz pozycję" size="small" />}
                        sx={{ mb: 2 }}
                    />

                    <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                        <TextField
                            label="Ilość"
                            type="number"
                            size="small"
                            fullWidth
                            value={form.quantity}
                            onChange={handleFieldChange('quantity')}
                        />
                        <TextField
                            label="Data"
                            type="date"
                            size="small"
                            fullWidth
                            value={form.transaction_date}
                            onChange={handleFieldChange('transaction_date')}
                        />
                    </Box>

                    {isPurchase && (
                        <>
                            <Box sx={{ mb: 2 }}>
                                <TextField
                                    label="Cena jednostkowa (zł)"
                                    type="number"
                                    size="small"
                                    fullWidth
                                    value={form.unit_price}
                                    onChange={handleFieldChange('unit_price')}
                                />
                            </Box>

                            <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5 }}>
                                Kontrahent / Dostawca
                            </Typography>
                            <Autocomplete
                                options={contractors}
                                loading={loadingContractors}
                                getOptionLabel={(option) =>
                                    option.nip ? `${option.name} (NIP: ${option.nip})` : option.name
                                }
                                filterOptions={(options, state) => {
                                    const inputValue = state.inputValue.toLowerCase();
                                    return options.filter(
                                        (opt) =>
                                            (opt.name && opt.name.toLowerCase().includes(inputValue)) ||
                                            (opt.nip && opt.nip.toLowerCase().includes(inputValue))
                                    );
                                }}
                                value={contractors.find((c) => c.id === form.contractor_id) ?? null}
                                onChange={(_, value) =>
                                    setForm((prev) => ({ ...prev, contractor_id: value?.id ?? null }))
                                }
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                renderInput={(params) => (
                                    <TextField {...params} placeholder="Wyszukaj po nazwie lub NIP" size="small" />
                                )}
                                sx={{ mb: 2 }}
                            />
                        </>
                    )}

                    <Typography sx={{ fontSize: '13px', color: 'text.secondary', mb: 0.5 }}>Uwagi</Typography>
                    <TextField
                        fullWidth
                        multiline
                        minRows={2}
                        placeholder="Opcjonalnie"
                        value={form.note}
                        onChange={handleFieldChange('note')}
                        sx={{ mb: 2 }}
                    />

                    <FormControlLabel
                        control={
                            <Checkbox
                                checked={form.is_correction}
                                onChange={(e) => setForm((prev) => ({ ...prev, is_correction: e.target.checked }))}
                            />
                        }
                        label="To korekta stanu (rzeczywisty stan różni się od stanu w systemie)"
                        sx={{ mb: 3 }}
                    />

                    {errorMsg && <Typography sx={{ color: '#C7433F', fontSize: '13px', mb: 2 }}>{errorMsg}</Typography>}

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1.5 }}>
                        <Button onClick={() => setModalOpen(false)} disabled={saving} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            Anuluj
                        </Button>
                        <Button variant="contained" onClick={handleSave} disabled={saving} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            {saving ? 'Zapisywanie...' : modalMode === 'przyjecie' ? 'Zarejestruj przyjęcie' : 'Zarejestruj wydanie'}
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>

            {/* Szczegóły transakcji */}
            <Dialog open={Boolean(detailsTarget)} onClose={() => setDetailsTarget(null)} maxWidth="xs" fullWidth PaperProps={{ sx: { borderRadius: 3 } }}>
                <DialogContent sx={{ p: 4 }}>
                    <Typography sx={{ fontSize: '18px', fontWeight: 700, mb: 2 }}>Szczegóły transakcji</Typography>
                    {detailsTarget?.is_correction && (
                        <Chip
                            label="Korekta"
                            size="small"
                            sx={{ bgcolor: '#FDF0D5', color: '#B8860B', fontWeight: 700, borderRadius: '8px', mb: 1.5 }}
                        />
                    )}
                    <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>Pozycja</Typography>
                    <Typography sx={{ fontWeight: 600, mb: 1.5 }}>{detailsTarget?.product_name}</Typography>

                    <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>Dokument</Typography>
                    <Typography sx={{ fontWeight: 600, mb: 1.5 }}>{detailsTarget?.document ?? '—'}</Typography>

                    {detailsTarget?.source && (
                        <>
                            <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>Źródło</Typography>
                            <Typography sx={{ fontWeight: 600, mb: 1.5 }}>{detailsTarget.source === 'produkcja' ? 'Z produkcji' : 'Zakupione'}</Typography>
                        </>
                    )}

                    {detailsTarget?.unit_price !== null && detailsTarget?.unit_price !== undefined && (
                        <>
                            <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>Cena jednostkowa</Typography>
                            <Typography sx={{ fontWeight: 600, mb: 1.5 }}>{detailsTarget.unit_price} zł</Typography>
                        </>
                    )}

                    {detailsTarget?.kontrahenci && (
                        <>
                            <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>Kontrahent</Typography>
                            <Typography sx={{ fontWeight: 600, mb: 1.5 }}>
                                {detailsTarget.contractor_display}
                            </Typography>
                        </>
                    )}

                    <Typography sx={{ fontSize: '13px', color: 'text.secondary' }}>Uwagi</Typography>
                    <Typography sx={{ fontWeight: 600, mb: 2 }}>{detailsTarget?.note || '—'}</Typography>

                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                        <Button variant="contained" onClick={() => setDetailsTarget(null)} sx={{ textTransform: 'none', fontWeight: 600, borderRadius: '10px', px: 2.5 }}>
                            Zamknij
                        </Button>
                    </Box>
                </DialogContent>
            </Dialog>
        </Box>
    );
}

export default WarehouseCategory;